import {
  createContext,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getEnvironmentName } from "lib/environment";

const DISABLE_1J_PARAM = "disable-1j";

interface Disable1jContextValue {
  active: boolean;
}

export const Disable1jContext = createContext<Disable1jContextValue>({
  active: false,
});

export function useIsDisable1jActive(): boolean {
  return useContext(Disable1jContext).active;
}

/**
 * Preserves the `disable-1j=true` query parameter across client-side navigations
 * within clockface (/cf/) routes. This is a non-production escape hatch that
 * forces PEO-enabled neon-dash routes to redirect back to clockwork.
 *
 * On each navigation, if the current URL contains `disable-1j=true` the param is
 * preserved on the next navigate. If the URL does not contain it, no action is taken.
 * Disabled entirely in production and sandbox environments.
 *
 * Provides `Disable1jContext` so consumers can read whether disable-1j is active
 * without inspecting the URL themselves.
 */
export default function PreserveDisable1j({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const prevHadParam = useRef<boolean>(false);

  const env = getEnvironmentName();
  const isNonProd = env !== "production" && env !== "sandbox";

  // Synchronous during render — so context consumers see the correct value immediately.
  // currentHasParam covers the initial load; prevHadParam.current covers re-renders after
  // navigate strips the param (the ref was set by the previous render's effect).
  const currentHasParam =
    isNonProd &&
    new URLSearchParams(location.search).get(DISABLE_1J_PARAM) === "true";
  const disable1jActive = currentHasParam || prevHadParam.current;

  useEffect(() => {
    if (!isNonProd) return;

    const params = new URLSearchParams(location.search);
    const hasParam = params.get(DISABLE_1J_PARAM) === "true";

    if (hasParam) {
      prevHadParam.current = true;
      return;
    }

    if (prevHadParam.current) {
      prevHadParam.current = false;
      params.set(DISABLE_1J_PARAM, "true");
      navigate(
        { pathname: location.pathname, search: params.toString() },
        { replace: true }
      );
    }
  }, [location.pathname, location.search, navigate, isNonProd]);

  return (
    <Disable1jContext.Provider value={{ active: disable1jActive }}>
      {children}
    </Disable1jContext.Provider>
  );
}
